'use client';

import { Check, Info, TriangleAlert } from 'lucide-react';
import React, { type ReactNode, useEffect, useRef, useState } from 'react';
import { toast as sonnerToast } from 'sonner';
import { capture } from '@/lib/analytics';
import { sanitizePII } from '@/lib/analytics/sanitize';
import { cn } from '@/lib/utils';

type ToastType = 'success' | 'error' | 'info';

const iconsByType: Record<ToastType, ReactNode> = {
  success: <Check className="text-positive" />,
  error: <TriangleAlert className="text-destructive" />,
  // Neutral/informational — not a success or a failure (e.g. "session
  // expired, please sign in"). Muted, non-alarming.
  info: <Info className="text-muted-foreground" />,
};

interface ToastOptions {
  description?: string;
}

interface ToastObjectProps {
  type: ToastType;
  description: string;
}

function showToast(type: ToastType, message: string, options?: ToastOptions) {
  const description = options?.description
    ? `${message}: ${options.description}`
    : message;

  // Track error toasts for analytics with PII sanitization
  if (type === 'error') {
    try {
      capture('error_displayed', {
        errorType: 'toast',
        message: sanitizePII(description),
      });
    } catch (error) {
      // Silently fail - analytics should never prevent toast display
      console.error('[Analytics] Failed to capture error toast:', error);
    }
  }

  return sonnerToast.custom(
    (id) => <Toast id={id} type={type} description={description} />,
    { duration: 4000 },
  );
}

/**
 * Toast wrapper that provides PII sanitization and analytics tracking.
 * Use this instead of importing directly from 'sonner'.
 *
 * Supports two APIs:
 * 1. Object API (legacy): toast({ type: 'error', description: 'message' })
 * 2. Method API (preferred): toast.error('message') or toast.success('message')
 *
 * @example
 * toast.error('Something went wrong')
 * toast.error('Failed to save', { description: 'Network error' })
 * toast.success('Saved successfully')
 * toast('Just a message') // defaults to success
 * toast({ type: 'error', description: 'Legacy API' }) // legacy object API
 */
export const toast = Object.assign(
  // Default function: supports both string and object API
  (messageOrProps: string | ToastObjectProps, options?: ToastOptions) => {
    if (typeof messageOrProps === 'object') {
      // Legacy object API: toast({ type: 'error', description: 'message' })
      return showToast(messageOrProps.type, messageOrProps.description);
    }
    // String API: toast('message')
    return showToast('success', messageOrProps, options);
  },
  {
    error: (message: string, options?: ToastOptions) =>
      showToast('error', message, options),
    success: (message: string, options?: ToastOptions) =>
      showToast('success', message, options),
    info: (message: string, options?: ToastOptions) =>
      showToast('info', message, options),
  },
);

function Toast(props: ToastProps) {
  const { id, type, description } = props;

  const descriptionRef = useRef<HTMLDivElement>(null);
  const [multiLine, setMultiLine] = useState(false);

  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;

    const update = () => {
      const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight);
      const lines = Math.round(el.scrollHeight / lineHeight);
      setMultiLine(lines > 1);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, [description]);

  return (
    <div className="flex w-full toast-mobile:w-[356px] justify-center">
      <div
        data-testid="toast"
        key={id}
        className={cn(
          'bg-background p-4 rounded-lg w-full toast-mobile:w-fit flex flex-row gap-3 border border-border shadow-md',
          multiLine ? 'items-start' : 'items-center',
        )}
      >
        <div
          data-type={type}
          className={cn('', {
            'pt-1': multiLine,
          })}
        >
          {iconsByType[type]}
        </div>
        <div
          ref={descriptionRef}
          className="text-foreground text-sm text-center"
        >
          {description}
        </div>
      </div>
    </div>
  );
}

interface ToastProps {
  id: string | number;
  type: ToastType;
  description: string;
}
